/**
 * The darkroom, in the side of the lightbox.
 *
 * Non-destructive, like the desktop's: what is stored is a rotation, a tone
 * adjustment and a request to colourise — a few dozen bytes — and never a
 * modified file. The original is untouched, and any edit can be undone years
 * later by somebody who did not make it. For a family archive that is not a
 * nicety: a photograph that has been "fixed" and cannot be un-fixed is a
 * photograph you have lost.
 *
 * ## Where the work happens
 *
 * In the container, not here. `edits::apply` is the desktop's own renderer —
 * the tone curve, the lens correction, the glare reduction, the two monochrome
 * conversions — and a canvas reimplementation would give two devices two
 * different photographs from one set of stored settings, which is the exact
 * failure storing settings was supposed to prevent.
 *
 * The cost is a round trip per preview, so the sliders do not render as they
 * move. They render when they are let go, which is also when somebody has
 * finished deciding.
 */

import { h, clear } from "./dom";
import { api, blobUrl, renditionUrl } from "./api";
import { store } from "./state";
import { toast } from "./ui";
import { icon } from "./icons";
import type { AssetCard, Edits } from "./types";

/** What a slider is, in one line each, so the panel is a table rather than code. */
const TONE: Array<{ key: "brightness" | "contrast" | "saturation"; label: string }> = [
  { key: "brightness", label: "Brightness" },
  { key: "contrast", label: "Contrast" },
  { key: "saturation", label: "Colour" },
];

/**
 * What `heirloom-core`'s `Mono` actually is.
 *
 * Three values, not the four a plausible-looking list would have. Serde
 * renames the variants to lowercase, and anything else here would be sent,
 * accepted as JSON, and silently parsed back to "none" — a control that
 * appears to work and does nothing.
 */
/**
 * Shapes a crop can be held to.
 *
 * Freehand is the default because most crops are "a bit off the left", but a
 * ratio is what somebody wants when the photograph is going somewhere with a
 * shape of its own — a 4×6 print, a square frame, a television. Getting 4:3 by
 * eye is possible and nobody manages it.
 *
 * The number is width ÷ height, and null is freehand.
 */
const RATIOS: Array<[string, string, number | null]> = [
  ["free", "Free", null],
  ["square", "Square", 1],
  ["43", "4:3", 4 / 3],
  ["34", "3:4", 3 / 4],
  ["32", "3:2", 3 / 2],
  ["169", "16:9", 16 / 9],
];

const MONO: Array<[string, string]> = [
  ["none", "Off"],
  ["bw", "Black & white"],
  ["sepia", "Sepia"],
];

export interface Darkroom {
  el: HTMLElement;
  /**
   * The crop rectangle, drawn over the photograph.
   *
   * Over it rather than beside it, for the same reason the page-split
   * rectangles are: a crop described by four numbers is arithmetic, and a crop
   * drawn on the picture is a decision about the picture.
   */
  overlay: HTMLElement;
  /** Called when a render lands, so the lightbox can swap the picture. */
  onPreview(fn: (url: string) => void): void;
  /** Browser-only tone preview while a slider is moving. */
  onTonePreview(fn: (edits: Edits) => void): void;
  /** Immediate quarter-turn preview while the full rendition is made. */
  onRotatePreview(fn: (quarters: number) => void): void;
}

export function darkroom(a: AssetCard): Darkroom {
  const el = h("div", { class: "darkroom" });
  const overlay = h("div", { class: "cropoverlay", hidden: true });

  /**
   * The line at the bottom, kept outside `build()`.
   *
   * It has to change the moment anything does — it said "nothing has been
   * changed, the original is always kept" underneath a photograph that had
   * just been turned, which is not a stale label, it is a false promise about
   * a photograph somebody has just edited. Rebuilding the whole panel to
   * refresh it would take focus off a slider mid-drag, so it is updated on its
   * own.
   */
  const note = h("p", { class: "dknote" });
  let edits: Edits = {};
  let glareFound: Array<{ x: number; y: number; w: number; h: number }> = [];
  let ratio: number | null = null;
  let busy = false;
  let show: ((url: string) => void) | null = null;
  let showTone: ((edits: Edits) => void) | null = null;
  let showRotate: ((quarters: number) => void) | null = null;
  let renderedRotate = 0;
  let confirmedEdits: Edits = {};
  let renderSyncing = false;
  let renderQueued = false;
  let renderWaiters: Array<(ok: boolean) => void> = [];
  let renderedKey: string | null = null;

  const changed = (): void => {
    window.dispatchEvent(new CustomEvent("heirloom:asset-edited", { detail: { assetId: a.id } }));
  };

  const say = (msg: string, bad = false): void => toast(msg, bad);

  /** True once anything has been changed from how the photograph arrived. */
  const touched = (): boolean => Object.keys(edits).length > 0;

  const sayState = (): void => {
    note.textContent = touched()
      ? "The original is untouched. Reset undoes all of this."
      : "Nothing has been changed. The original is always kept.";
  };

  const working = (on: boolean): void => {
    busy = on;
    el.classList.toggle("busy", on);
    for (const c of el.querySelectorAll("button,input,select")) {
      if (on) c.setAttribute("disabled", "");
      else c.removeAttribute("disabled");
    }
  };

  const renderWorking = (on: boolean): void => {
    el.classList.toggle("syncing", on);
    el.setAttribute("aria-busy", String(on));
  };

  const previewRotation = (): void => {
    const wanted = edits.rotate ?? 0;
    showRotate?.((wanted - renderedRotate + 4) % 4);
  };

  /** Save only the newest visible state and never flash an older rendition. */
  const render = (): Promise<boolean> => {
    renderQueued = true;
    const settled = new Promise<boolean>((resolve) => renderWaiters.push(resolve));
    if (renderSyncing) return settled;
    renderSyncing = true;
    renderWorking(true);
    void (async (): Promise<void> => {
      let succeeded = true;
      try {
        while (renderQueued) {
          renderQueued = false;
          const requested = structuredClone(edits);
          const { key } = await api.renderEdits(store.libraryId, a.id, requested);
          confirmedEdits = requested;
          if (key) renderedKey = key;
          if (!renderQueued) {
            if (key) {
              renderedRotate = requested.rotate ?? 0;
              showRotate?.(0);
            }
            if (key && show && overlay.hidden) {
              show(renditionUrl(store.libraryId, a.id, "edited", key));
            }
          }
          changed();
        }
      } catch (e) {
        succeeded = false;
        renderQueued = false;
        edits = structuredClone(confirmedEdits);
        renderedRotate = edits.rotate ?? 0;
        showTone?.(edits);
        showRotate?.(0);
        if (renderedKey && show && overlay.hidden) {
          show(renditionUrl(store.libraryId, a.id, "edited", renderedKey));
        }
        build();
        paintCrop();
        say(e instanceof Error ? e.message : "could not apply that", true);
      } finally {
        renderSyncing = false;
        renderWorking(false);
        const waiters = renderWaiters;
        renderWaiters = [];
        for (const resolve of waiters) resolve(succeeded);
      }
    })();
    return settled;
  };

  const waitForRender = (): Promise<boolean> => {
    if (!renderSyncing) return Promise.resolve(true);
    return new Promise((resolve) => renderWaiters.push(resolve));
  };

  const set = <K extends keyof Edits>(key: K, value: Edits[K]): void => {
    // A field back at its default is removed rather than stored as zero, so
    // "nobody has touched this" stays distinguishable from "somebody looked
    // and decided nothing was needed" — which is what decides whether the
    // automatic pass offers itself.
    const isDefault =
      value === 0 || value === false || value === null || value === undefined;
    if (isDefault) delete edits[key];
    else edits[key] = value;
    sayState();
  };

  /**
   * Draw, move and resize the crop rectangle.
   *
   * One rectangle, not many: a crop is a single decision about where a
   * photograph ends. Dragging on an empty part starts a new one, which is also
   * how you replace a crop you do not like — there is no separate "start
   * again" to find.
   */
  const paintCrop = (): void => {
    clear(overlay);
    const c = edits.crop;
    if (!c) return;
    const box = h("div", {
      class: "croprect",
      style: `left:${c.x * 100}%;top:${c.y * 100}%;width:${c.w * 100}%;height:${c.h * 100}%`,
    });
    box.appendChild(h("span", { class: "crophandle" }));
    box.addEventListener("pointerdown", (ev: PointerEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      const rect = overlay.getBoundingClientRect();
      const corner =
        ev.clientX > rect.left + (c.x + c.w) * rect.width - 14 &&
        ev.clientY > rect.top + (c.y + c.h) * rect.height - 14;
      const sx = ev.clientX;
      const sy = ev.clientY;
      const from = { ...c };
      const move = (m: PointerEvent): void => {
        const dx = (m.clientX - sx) / rect.width;
        const dy = (m.clientY - sy) / rect.height;
        edits.crop = corner
          ? clampCrop({ x: from.x, y: from.y, w: from.w + dx, h: from.h + dy })
          : clampCrop({
              x: Math.max(0, Math.min(1 - from.w, from.x + dx)),
              y: Math.max(0, Math.min(1 - from.h, from.y + dy)),
              w: from.w,
              h: from.h,
            });
        paintCrop();
      };
      const up = (): void => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        sayState();
        void render();
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    });
    overlay.appendChild(box);
  };

  /**
   * The width-to-height a crop must have, as a fraction of the frame.
   *
   * The ratio the user asked for is in *pixels*; a crop is stored as fractions
   * of the frame. On a 3:2 photograph a crop of 0.5 × 0.5 is 3:2 and not
   * square, so the frame's own aspect converts between the two — and without
   * it "Square" would produce a square only on a square photograph, which is a
   * control that appears to do nothing on the one shape nobody tests with.
   */
  const targetFraction = (): number | null => {
    if (!ratio) return null;
    const box = overlay.getBoundingClientRect();
    const frame = box.height > 0 ? box.width / box.height : 1;
    return ratio / frame;
  };

  /** Keep a rectangle inside the frame, ratio or not. */
  const fit = (c: { x: number; y: number; w: number; h: number }) => ({
    x: Math.max(0, Math.min(1 - Math.min(c.w, 1), c.x)),
    y: Math.max(0, Math.min(1 - Math.min(c.h, 1), c.y)),
    w: Math.min(1, c.w),
    h: Math.min(1, c.h),
  });

  /**
   * Re-shape a rectangle to the chosen ratio, keeping its area.
   *
   * Area rather than one edge, because this runs when somebody *presses* a
   * shape and both answers are arbitrary — but only this one is idempotent.
   * Deriving the height from the width shrinks the rectangle a little every
   * time the same button is pressed, which is what the first version did: two
   * presses of "Square" took a sensible crop down to four pixels by seventeen.
   */
  const shapeCrop = (c: { x: number; y: number; w: number; h: number }) => {
    const target = targetFraction();
    if (!target) return fit(c);
    const area = Math.max(1e-6, c.w * c.h);
    let h = Math.sqrt(area / target);
    let w = target * h;
    // Too big for the frame once re-shaped: shrink both, keeping the shape.
    const over = Math.max(w, h, 1);
    w /= over;
    h /= over;
    // Around the middle of where it was, so a re-shape does not walk away
    // from the thing being cropped.
    return fit({ x: c.x + (c.w - w) / 2, y: c.y + (c.h - h) / 2, w, h });
  };

  /**
   * Tidy a rectangle as it is being dragged.
   *
   * With a ratio chosen the height follows the width, so horizontal movement
   * drives the shape — one axis leading is predictable, and two axes
   * negotiating is a rectangle that fights the pointer.
   */
  const clampCrop = (c: { x: number; y: number; w: number; h: number }) => {
    const x = Math.max(0, Math.min(1, Math.min(c.x, c.x + c.w)));
    const y = Math.max(0, Math.min(1, Math.min(c.y, c.y + c.h)));
    let w = Math.min(1, Math.abs(c.w));
    let h = Math.min(1, Math.abs(c.h));
    const target = targetFraction();
    if (target) h = Math.min(1, w / target);
    return fit({ x, y, w, h });
  };

  overlay.addEventListener("pointerdown", (ev: PointerEvent) => {
    if (busy) return;
    ev.preventDefault();
    const rect = overlay.getBoundingClientRect();
    const x0 = (ev.clientX - rect.left) / rect.width;
    const y0 = (ev.clientY - rect.top) / rect.height;
    const move = (m: PointerEvent): void => {
      edits.crop = clampCrop({
        x: x0,
        y: y0,
        w: (m.clientX - rect.left) / rect.width - x0,
        h: (m.clientY - rect.top) / rect.height - y0,
      });
      paintCrop();
    };
    const up = (): void => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      // A click rather than a drag is not a crop of nothing, it is a click.
      const c = edits.crop;
      if (!c || c.w < 0.03 || c.h < 0.03) delete edits.crop;
      paintCrop();
      sayState();
      void render();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  });

  const build = (): void => {
    clear(el);

    // --- the three anybody actually presses --------------------------------
    //
    // Above the dials, not below them. Auto is the first thing most people
    // want and Reset is the thing they want when it went wrong, and both were
    // under five sliders — which is a scroll away in a panel this tall, and
    // exactly where somebody looking for "undo this" will not look.
    const acts = h("div", { class: "dkacts" });

    acts.appendChild(
      h(
        "button",
        {
          class: edits.colorize ? "btn sm on" : "btn sm",
          title: "Measure what this photograph needs and show it — nothing is kept until you say so",
          onclick: () => {
            void (async () => {
              if (busy) return;
              working(true);
              try {
                await waitForRender();
                const { proposed } = await api.autoEdits(store.libraryId, a.id, edits);
                edits = proposed;
                build();
                if (!(await render())) return;
                say("Proposed. Reset puts it back.");
              } catch (e) {
                say(e instanceof Error ? e.message : "could not work that out", true);
              } finally {
                working(false);
              }
            })();
          },
        },
        icon("sparkle", "i sm"),
        "Auto",
      ),
    );

    acts.appendChild(
      h(
        "button",
        {
          class: "btn sm",
          // Said plainly on the button's title rather than discovered
          // afterwards: measured on this archive the colouriser is good on
          // landscapes and skin and confidently wrong about the colour of a
          // dress or a uniform.
          title:
            "Guess the colours of a black-and-white photograph. It is often " +
            "right about skin and landscape and often wrong about clothing — " +
            "Reset puts it back.",
          onclick: () => {
            void (async () => {
              if (busy) return;
              working(true);
              try {
                await waitForRender();
                const { key } = await api.colouriseAsset(store.libraryId, a.id);
                set("colorize", true);
                // Shown straight away rather than only after a render: with no
                // other edits on the photograph, `apply` is the identity and a
                // render would return the same bytes for a second round trip.
                if (Object.keys(edits).some((field) => field !== "colorize")) {
                  if (!(await render())) return;
                }
                else {
                  await api.saveEdits(store.libraryId, a.id, edits);
                  if (show) show(renditionUrl(store.libraryId, a.id, "colorized", key));
                  changed();
                }
                build();
                say("Coloured. Reset puts it back.");
              } catch (e) {
                say(e instanceof Error ? e.message : "could not colour that", true);
              } finally {
                working(false);
              }
            })();
          },
        },
        icon("palette", "i sm"),
        edits.colorize ? "Colour added" : "Colourise",
      ),
    );

    acts.appendChild(
      h("button", {
        class: "btn sm ghost",
        text: "Reset",
        title: "Back to the photograph exactly as it arrived",
        onclick: () => {
          void (async () => {
            if (busy) return;
            working(true);
            try {
              await waitForRender();
              await api.resetEdits(store.libraryId, a.id);
              edits = {};
              confirmedEdits = {};
              renderedKey = null;
              build();
              const original = blobUrl(a.contentHash);
              if (original && show) show(original);
              changed();
              say("Back to the original.");
            } catch (e) {
              say(e instanceof Error ? e.message : "could not reset it", true);
            } finally {
              working(false);
            }
          })();
        },
      }),
    );

    el.appendChild(acts);

    // --- turning ---------------------------------------------------------
    const turn = h(
      "div",
      { class: "dkrow" },
      h("span", { class: "dklbl", text: "Turn" }),
      h(
        "div",
        { class: "dkbtns" },
        h(
          "button",
          {
            class: "btn xs",
            "data-turn": "true",
            title: "Turn a quarter turn anticlockwise",
            onclick: () => {
              set("rotate", (((edits.rotate ?? 0) + 3) % 4) as Edits["rotate"]);
              previewRotation();
              void render();
            },
          },
          icon("rotate-left", "i sm"),
        ),
        h(
          "button",
          {
            class: "btn xs",
            "data-turn": "true",
            title: "Turn a quarter turn clockwise",
            onclick: () => {
              set("rotate", (((edits.rotate ?? 0) + 1) % 4) as Edits["rotate"]);
              previewRotation();
              void render();
            },
          },
          icon("rotate-right", "i sm"),
        ),
      ),
    );
    el.appendChild(turn);

    // --- tone ------------------------------------------------------------
    for (const { key, label } of TONE) {
      const value = (edits[key] as number | undefined) ?? 0;
      const out = h("span", { class: "dkval", text: value.toFixed(2) });
      const input = h("input", {
        type: "range",
        min: "-1",
        max: "1",
        step: "0.02",
        value: String(value),
        // Live while dragging, rendered when let go. A round trip per
        // pixel of travel would be a hundred renders for one decision.
        oninput: (e: Event) => {
          const value = Number((e.target as HTMLInputElement).value);
          out.textContent = value.toFixed(2);
          showTone?.({ ...edits, [key]: value });
        },
        onchange: (e: Event) => {
          set(key, Number((e.target as HTMLInputElement).value));
          void render();
        },
      });
      el.appendChild(
        h("div", { class: "dkrow" }, h("span", { class: "dklbl", text: label }), input, out),
      );
    }

    // --- a deliberate look ------------------------------------------------
    el.appendChild(
      h(
        "div",
        { class: "dkrow" },
        h("span", { class: "dklbl", text: "Look" }),
        h(
          "select",
          {
            onchange: (e: Event) => {
              const v = (e.target as HTMLSelectElement).value as "none" | "bw" | "sepia";
              set("mono", v === "none" ? null : v);
              void render();
            },
          },
          ...MONO.map(([value, text]) =>
            h("option", { value, selected: (edits.mono ?? "none") === value, text }),
          ),
        ),
      ),
    );

    // --- what to keep -----------------------------------------------------
    const cropping = overlay.hidden === false;
    el.appendChild(
      h(
        "div",
        { class: "dkrow" },
        h("span", { class: "dklbl", text: "Crop" }),
        h(
          "div",
          { class: "dkbtns" },
          h("button", {
            class: cropping ? "btn xs on" : "btn xs",
            text: cropping ? "Done" : "Trim",
            title: cropping
              ? "Stop drawing — the crop stays"
              : "Draw a rectangle on the photograph to keep only part of it",
            onclick: () => {
              overlay.hidden = cropping;
              build();
              paintCrop();
              // Finishing shows what the crop actually did, which the
              // rectangle could only promise.
              if (overlay.hidden) void render();
            },
          }),
          edits.crop
            ? h("button", {
                class: "btn xs ghost",
                text: "Whole frame",
                title: "Back to the full photograph",
                onclick: () => {
                  delete edits.crop;
                  paintCrop();
                  sayState();
                  void render();
                },
              })
            : null,
        ),
      ),
    );

    // The shape to hold it to, shown only while cropping. A row of ratios on
    // a photograph nobody is cropping is a row of buttons that do nothing.
    if (cropping) {
      const row = h("div", { class: "dkbtns dkratios" });
      for (const [key, label, value] of RATIOS) {
        row.appendChild(
          h("button", {
            class: (ratio ?? null) === value ? "btn xs on" : "btn xs",
            text: label,
            "data-ratio": key,
            onclick: () => {
              ratio = value;
              // Re-shape what is already drawn, rather than waiting for the
              // next drag: choosing 4:3 means "make this 4:3".
              if (edits.crop) {
                edits.crop = shapeCrop(edits.crop);
                paintCrop();
                void render();
              }
              build();
            },
          }),
        );
      }
      el.appendChild(h("div", { class: "dkrow" }, h("span", { class: "dklbl", text: "Shape" }), row));
    }

    // --- sharpening and the lens --------------------------------------------
    //
    // Both are in `Edits` and both are set by Auto, and neither had a control
    // — so somebody could see that Auto had sharpened a scan and had no way to
    // say "less than that". A value the machine can set and a person cannot is
    // a value the person does not own.
    for (const [key, label, hint] of [
      ["sharpen", "Sharpen", "Unsharp masking. A scan that went soft, not a photograph that was."],
    ] as Array<["sharpen", string, string]>) {
      const value = (edits[key] as number | undefined) ?? 0;
      const out = h("span", { class: "dkval", text: value.toFixed(2) });
      el.appendChild(
        h(
          "div",
          { class: "dkrow" },
          h("span", { class: "dklbl", text: label, title: hint }),
          h("input", {
            type: "range",
            min: "0",
            max: "1",
            step: "0.05",
            value: String(value),
            oninput: (e: Event) => {
              out.textContent = Number((e.target as HTMLInputElement).value).toFixed(2);
            },
            onchange: (e: Event) => {
              set(key, Number((e.target as HTMLInputElement).value));
              void render();
            },
          }),
          out,
        ),
      );
    }

    // The lens: three numbers on one nested object, so they are set together
    // rather than each replacing the others.
    const lens = (edits.lens ?? {}) as { vignette?: number; distortion?: number; defringe?: number };
    const lensRow = (
      field: "vignette" | "distortion",
      label: string,
      min: string,
      hint: string,
    ): HTMLElement => {
      const value = lens[field] ?? 0;
      const out = h("span", { class: "dkval", text: value.toFixed(2) });
      return h(
        "div",
        { class: "dkrow" },
        h("span", { class: "dklbl", text: label, title: hint }),
        h("input", {
          type: "range",
          min,
          max: "1",
          step: "0.05",
          value: String(value),
          oninput: (e: Event) => {
            out.textContent = Number((e.target as HTMLInputElement).value).toFixed(2);
          },
          onchange: (e: Event) => {
            const v = Number((e.target as HTMLInputElement).value);
            const next = { ...lens, [field]: v };
            // Every field at zero means no lens correction at all, and that is
            // stored as absent rather than as a lens of zeroes — same rule as
            // the tone sliders, so "nobody has touched this" stays a state.
            const any = Object.values(next).some((n) => n && n !== 0);
            if (any) edits.lens = next;
            else delete edits.lens;
            sayState();
            void render();
          },
        }),
        out,
      );
    };
    el.appendChild(
      lensRow("vignette", "Dark edges", "0", "Brighten lens shading around the outside of a scan."),
    );
    el.appendChild(
      lensRow("distortion", "Straighten", "-1", "Bow the edges back. Positive corrects barrel."),
    );

    // --- glare -------------------------------------------------------------
    //
    // Only offered where the container actually found some. A control that is
    // there on every photograph and does nothing on most of them is a control
    // people stop reading.
    if (glareFound.length) {
      const on = (edits.glare?.length ?? 0) > 0;
      el.appendChild(
        h(
          "div",
          { class: "dkrow" },
          h("span", { class: "dklbl", text: "Glare" }),
          h("button", {
            class: on ? "btn xs on" : "btn xs",
            text: on ? `Reducing ${glareFound.length}` : `Reduce ${glareFound.length}`,
            title:
              "Patches of flash or window reflection the pass found. Toggling " +
              "this is not permanent — the original is kept.",
            onclick: () => {
              if (on) delete edits.glare;
              else edits.glare = glareFound.map((g) => ({ ...g }));
              build();
              sayState();
              void render();
            },
          }),
        ),
      );
    }



    sayState();
    el.appendChild(note);
  };

  // What is already on it, if anything.
  void api
    .getEdits(store.libraryId, a.id)
    .then((stored) => {
      edits = stored.edits ?? {};
      confirmedEdits = structuredClone(edits);
      renderedRotate = edits.rotate ?? 0;
      glareFound = stored.glareFound ?? [];
      build();
      paintCrop();
      if (!show) return;
      // Whichever exists: a render when there is one, the colourised working
      // image when colour was asked for and nothing else has been changed.
      if (stored.editedKey) {
        renderedKey = stored.editedKey;
        show(renditionUrl(store.libraryId, a.id, "edited", stored.editedKey));
      } else if (stored.colorizedKey) {
        show(renditionUrl(store.libraryId, a.id, "colorized", stored.colorizedKey));
      }
    })
    .catch(() => build());

  build();

  return {
    el,
    overlay,
    onPreview(fn) {
      show = fn;
    },
    onTonePreview(fn) {
      showTone = fn;
    },
    onRotatePreview(fn) {
      showRotate = fn;
    },
  };
}
